import * as d3 from 'd3';
import {Axis, Selection} from 'd3';
import {ScaleLinear} from 'd3-scale';
import debounce from 'lodash.debounce';
import {Component} from 'lowkey-component';

import {MediaQuery} from '@services/MediaQuery';
import {ViewportObserver} from '@services/ViewportObserver';
import {WindowService} from '@services/WindowService';

export class AgeGroup extends Component {
  mq = MediaQuery.create();
  _viewportObserver: ViewportObserver;
  target: SVGSVGElement | null = null;
  svg: Selection<any, any, any, any> | null = null;
  data: any[] = [];
  isAnimated = false;
  isAnimation = false;
  window = WindowService.create();

  xScale: ScaleLinear<any, any> | null = null;
  yScale: ScaleLinear<any, any> | null = null;
  xAxis: Axis<any> | null = null;
  yAxis: Axis<any> | null = null;

  constructor(element: HTMLDivElement) {
    super(element);

    this.resize = debounce(this.resize.bind(this), 500, {leading: true});
    this._viewportObserver = new ViewportObserver({margin: '-30%', ratio: 0.1});
  }

  didMount() {
    setTimeout(() => {
      const top = this.element.getBoundingClientRect().top + window.pageYOffset;
      const isOver = window.pageYOffset + window.innerHeight > top;
      if (isOver) {
        this.animate();
      } else {
        this._viewportObserver.onChange.connect((status) => {
          if (status === 'below') {
            this.animate();
          }
        });
      }
    }, 100); // msecまたないとwindow.pageYOffsetが0
    this._viewportObserver.observe(this.element);

    this.target = this.element.querySelector('svg') as SVGSVGElement;

    this.data = [
      {label: 10, value: 1},
      {label: 20, value: 67},
      {label: 30, value: 19},
      {label: 40, value: 9},
      {label: 50, value: 4},
    ];

    this.svg = d3.select('#' + this.target.getAttribute('id'));

    // this.maxValue = d3.max(this.data, function (d) {
    //   return d.value
    // })
    this.xAxis_y = 20;
    this.yAxis_x = -20;
    // this.margin = {top: 30, right: 20, bottom: 50, left: 80}
    this.margin = {top: 54, right: 0, bottom: 32, left: 0};
    this.barWidth = 50;
    this.width =
      parseInt(this.svg.style('width')) - this.margin.left - this.margin.right;
    this.height =
      parseInt(this.svg.style('height')) - this.margin.top - this.margin.bottom;

    this.render();
    this.resize();
    this.window.onResize.connect(this.resize);
  }

  render() {
    this.svg.selectAll('*').remove();

    this.defs = this.svg.append('defs');

    this.xScale = d3
      .scaleBand()
      .domain(
        this.data.map(function (d) {
          return d.label;
        }),
      )
      .range([0, this.width - this.margin.right])
      .padding(this.mq.isPhone() ? 0.2 : 0.1);

    this.yScale = d3
      .scaleLinear()
      .range([this.height, 0])
      .domain([0, d3.max(this.data, (d) => d.value)]);

    this.xAxis = d3.axisBottom(this.xScale);

    this.yAxis = d3
      .axisLeft(this.yScale)
      .tickValues([0, 60])
      .ticks(5)
      .tickValues([0, 300, 400, 500, 600, 700])
      .tickFormat((d) => {
        return d < 300 ? 0 : d;
      });

    this.g = this.svg
      .append('g')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    // 棒
    this.bar1 = this.g
      .selectAll('bar')
      .data(this.data)
      .enter()
      .append('rect')
      .attr('fill', 'rgba(0, 0 ,0 ,0.8)')
      .attr('width', this.xScale.bandwidth())
      .attr('height', 0)
      .attr('x', (d) => {
        return this.xScale(d.label);
      })
      .attr('y', (d, i) => {
        return this.height;
      })
      .attr('opacity', (d) => {
        const scale = d3.scaleLinear().domain([0, 60]).range([0.6, 1.0]);
        return scale(d.value);
      });

    // 棒グラフのラベル
    this.g
      .selectAll('text')
      .data(this.data)
      .enter()
      .append('text')
      .style('text-anchor', 'middle')
      .attr('x', (d) => {
        return this.xScale(d.label) + this.xScale.bandwidth() / 2;
      })
      .attr('y', (d) => {
        return this.yScale(d.value) - 10;
      })
      .html((d) => {
        return d.value + '<tspan>%</tspan>';
      })
      .attr('class', 'value')
      .attr('opacity', 0);

    // 横軸のラベル
    this.g
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(this.xAxis)
      .call((g) => g.select('.domain').remove())
      .call((g) => g.selectAll('line').remove())
      .selectAll('text')
      .attr('class', 'label')
      .style('text-anchor', 'middle')
      .attr('dy', '18px')
      .text((d) => {
        return `${d}代`;
      })
      .attr('opacity', 0);
  }

  resize() {
    this.width =
      parseInt(this.svg.style('width')) - this.margin.left - this.margin.right;
    this.height =
      parseInt(this.svg.style('height')) - this.margin.top - this.margin.bottom;
    this.svg
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom);

    this.xScale.range([0, this.width - this.margin.right]);
    this.yScale.range([this.height, 0]);

    // 罫線の更新
    this.xAxis.tickSize(-this.height);
    this.yAxis.tickSize(-this.width + this.margin.right);

    // 横軸の位置
    this.g
      .selectAll('.x-axis')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(this.xAxis)
      .call((g) => g.select('.domain').remove())
      .call((g) => g.selectAll('line').remove())
      .selectAll('text')
      .attr('dy', '18px')
      .text((d) => {
        return `${d}代`;
      });

    this.g
      .selectAll('.value')
      // .style('text-anchor', 'middle')
      .attr('x', (d) => {
        return this.xScale(d.label) + this.xScale.bandwidth() / 2;
      })
      .attr('y', (d) => {
        return this.yScale(d.value) - 10;
      });
    // .html((d) => {
    //   return d.value + '<tspan>%</tspan>'
    // })

    this.g.selectAll('.axis-y').call(this.yAxis);

    if (this.isAnimated) {
      this.bar1
        .attr('height', (d) => this.yScale(0) - this.yScale(d.value))
        .attr('y', (d, i) => {
          return this.yScale(d.value);
        });
    }
    this.bar1.attr('width', this.xScale.bandwidth()).attr('x', (d) => {
      return this.xScale(d.label);
    });
  }

  animate() {
    if (this.isAnimation || this.isAnimated) {
      return;
    }

    this.element.classList.add('viewed');
    this.isAnimation = true;

    this.svg
      .selectAll('.value')
      .transition()
      .ease(d3.easeCubicOut)
      .delay((d, i) => 1000 + i * 80)
      .duration(1000)
      .attr('opacity', 1);

    this.svg
      .selectAll('.label')
      .transition()
      .ease(d3.easeCubicOut)
      .delay(1000)
      .duration(1000)
      .attr('opacity', 1);

    this.bar1
      .transition()
      .duration(1000)
      .delay((d, i) => i * 40)
      .attr('y', (d) => {
        return this.yScale(d.value);
      })
      .attr('height', (d) => {
        return this.yScale(0) - this.yScale(d.value);
      })
      .on('end', () => {
        this.isAnimated = true;
      });

    this._viewportObserver.disconnect();
  }

  willUnmount() {
    this.window.onResize.disconnect(this.resize);
  }

  didUnmount() {
    this._viewportObserver.disconnect();
  }
}
